/**
 * Exportación CSV / Excel para la vista previa «Procesar a SAP» (validación precio/stock desde API).
 * Una sola tabla sintética por hoja; mismas columnas para las 3 variantes (solo cambia el subconjunto de filas).
 */
import ExcelJS from 'exceljs';
import { bulkPreviewRowEligibleForCleanPriceStockExport } from './bulkPreviewExportEligibility';

const FILL_WARNING = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFF9C4' }
};

const FILL_ERROR = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFCDD2' }
};

/**
 * Orden de campos alineado con las etiquetas que pasa la UI (`columnLabels`).
 * @param {object} row - fila vista previa SAP (`sapChilePreviewFlatRows`)
 * @returns {unknown[]}
 */
export function sapProcessPreviewRowToCells(row) {
  const r = row && typeof row === 'object' ? row : {};
  const discPct = r.brandDiscountPercent;
  const descuentoCell =
    r.precioListaSap != null && discPct != null && Number.isFinite(Number(discPct))
      ? `${Number(discPct)}%`
      : '';
  return [
    r.rowIndex ?? '',
    r.validationStatus ?? '',
    r.nOc ?? '',
    r.tiendaDestino ?? '',
    r.fechaOc ?? '',
    r.skuIac ?? '',
    r.descripcion ?? '',
    r.cantOc ?? '',
    r.precioAcordado ?? '',
    r.totalLinea ?? '',
    r.precioListaSap ?? '',
    descuentoCell,
    r.precioListaSapConDescuento ?? '',
    r.stockDispSap ?? '',
    r.validationMessage ?? ''
  ];
}

/**
 * Solo líneas con error o advertencia (no `ok`).
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
export function filterSapProcessPreviewProblemRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((r) => r && r.validationStatus && r.validationStatus !== 'ok');
}

/**
 * Sin líneas con validación `error` (conserva `ok` y `warning`).
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
export function filterSapProcessPreviewRowsWithoutErrors(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((r) => r && r.validationStatus !== 'error');
}

/**
 * Filas sin `error` que podían usarse como referencia “relajada” (ok + warning solo por precio).
 * La exportación «precio/stock OK» del modal SAP usa `bulkPreviewRowEligibleForCleanPriceStockExport` (módulo `bulkPreviewExportEligibility`).
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
export function filterSapProcessPreviewRowsExportableForSap(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((r) => {
    if (!r || !r.validationStatus) return false;
    if (r.validationStatus === 'error') return false;
    if (r.validationStatus === 'ok') return true;
    if (r.validationStatus === 'warning') return r.sapPriceMismatch === true;
    return false;
  });
}

/**
 * Solo líneas con validación `ok` (excluye errores y advertencias).
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
export function filterSapProcessPreviewRowsOkOnly(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((r) => r && r.validationStatus === 'ok');
}

/**
 * CSV separado por `;`, comillas como en carga mayorista.
 *
 * @param {object[]} rows - típicamente solo problemas (`filterSapProcessPreviewProblemRows`)
 * @param {string[]} columnLabels - encabezados ya traducidos
 * @returns {Blob}
 */
export function buildSapProcessPreviewProblemRowsCsvBlob(rows, columnLabels) {
  const headers = Array.isArray(columnLabels) ? columnLabels : [];
  const dataRows = Array.isArray(rows) ? rows : [];
  const esc = (c) => `"${String(c).replace(/"/g, '""')}"`;
  const lines = [
    headers.join(';'),
    ...dataRows.map((r) => sapProcessPreviewRowToCells(r).map(esc).join(';'))
  ];
  return new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
}

/**
 * Excel: todas las filas; error/advertencia resaltadas; última columna = mensaje validación.
 *
 * @param {object[]} rows
 * @param {string[]} columnLabels
 * @returns {Promise<ArrayBuffer>}
 */
export async function buildSapProcessPreviewMarkedXlsxBuffer(rows, columnLabels) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Validación SAP', {
    views: [{ state: 'frozen', ySplit: 1 }]
  });

  const labels = Array.isArray(columnLabels) ? columnLabels : [];
  const headerRow = ws.getRow(1);
  labels.forEach((label, i) => {
    const c = headerRow.getCell(i + 1);
    c.value = label;
    c.font = { bold: true };
  });

  const list = Array.isArray(rows) ? rows : [];
  for (let i = 0; i < list.length; i += 1) {
    const row = list[i];
    const excelRow = ws.getRow(i + 2);
    const cells = sapProcessPreviewRowToCells(row);
    for (let j = 0; j < cells.length; j += 1) {
      const v = cells[j];
      excelRow.getCell(j + 1).value = v === undefined || v === null ? '' : v;
    }
    const st = row?.validationStatus;
    const fill = st === 'error' ? FILL_ERROR : st === 'warning' ? FILL_WARNING : null;
    if (fill) {
      for (let c = 1; c <= labels.length; c += 1) {
        excelRow.getCell(c).fill = fill;
      }
    }
  }

  ws.columns.forEach((col) => {
    let max = 12;
    col.eachCell({ includeEmpty: false }, (cell) => {
      const len = cell.value != null ? String(cell.value).length : 0;
      if (len > max) max = Math.min(len, 60);
    });
    col.width = Math.min(Math.max(max + 2, 12), 45);
  });

  return wb.xlsx.writeBuffer();
}

/**
 * Excel: mismas columnas que el marcado completo; solo filas validadas `ok` (sin errores ni advertencias)
 * (`bulkPreviewRowEligibleForCleanPriceStockExport` desde `bulkPreviewExportEligibility`).
 *
 * @param {object[]} rows
 * @param {string[]} columnLabels
 * @returns {Promise<ArrayBuffer>}
 */
export async function buildSapProcessPreviewXlsxWithoutErrorRowsBuffer(rows, columnLabels) {
  const filtered = (Array.isArray(rows) ? rows : []).filter(bulkPreviewRowEligibleForCleanPriceStockExport);
  return buildSapProcessPreviewMarkedXlsxBuffer(filtered, columnLabels);
}
